import React, { useEffect, useRef } from 'react'
import { IoClose } from 'react-icons/io5'
import { BsFillInfoCircleFill, BsPatchCheckFill } from 'react-icons/bs'
import { PiWarningFill } from 'react-icons/pi'
export type ConfirmDialogType = 'success' | 'error' | 'warning' | 'info'

export interface ConfirmDialogButton {
    label: string
    variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'ghost'
    onClick: () => void
    disabled?: boolean
    loading?: boolean
}

export interface ConfirmDialogProps {
    open: boolean
    type?: ConfirmDialogType
    title: string
    message?: string | React.ReactNode
    buttons: ConfirmDialogButton[]
    onClose?: () => void
    closeOnBackdropClick?: boolean
    closeOnEscape?: boolean
    showCloseButton?: boolean
    className?: string
}

// Type configuration
const typeConfig: Record<
    ConfirmDialogType,
    {
        icon: React.ReactNode
        gradient: string
        closeIconColor: string
        closeIconHoverColor: string
        closeIconHoverBgColor: string
        titleColor?: string
    }
> = {
    success: {
        icon: (
            <div className="relative text-[2.5rem] flex-shrink-0">
                <div className="bg-white absolute top-[0.25em] left-[0.3em] w-[0.4em] h-[0.5em] z-0"></div>
                <BsPatchCheckFill className="text-green-700 absolute z-10 drop-shadow-[0_0_6px_rgba(255,255,255,0.7)]" />
                <BsPatchCheckFill opacity={0} />
            </div>
        ),
        titleColor: 'text-gray-900',
        gradient: 'bg-gradient-to-r from-[#22c55e] to-[#86efac]',
        closeIconColor: 'text-green-600',
        closeIconHoverColor: 'hover:text-green-600',
        closeIconHoverBgColor: 'hover:bg-green-100',
    },
    error: {
        icon: (
            <div className="relative text-[2.5rem] flex-shrink-0">
                <div className="bg-[#282728] absolute top-[0.3em] left-[0.33em] w-[0.35em] h-[0.5em] z-0"></div>
                <PiWarningFill className="text-yellow-400 absolute z-10 drop-shadow-[0_0_6px_rgba(0,0,0,0.5)]" />
                <PiWarningFill opacity={0} />
            </div>
        ),
        titleColor: 'text-red-100',
        gradient: 'bg-gradient-to-r from-[#cf0504] to-[#f00606]',
        closeIconColor: 'text-red-200',
        closeIconHoverColor: 'hover:text-red-600',
        closeIconHoverBgColor: 'hover:bg-red-100',
    },
    warning: {
        icon: (
            <div className="relative text-[2.5rem] flex-shrink-0">
                <div className="bg-white absolute top-[0.3em] left-[0.33em] w-[0.35em] h-[0.5em] z-0"></div>
                <PiWarningFill className="text-[#282728] absolute z-10 drop-shadow-[0_0_6px_rgba(255,255,255,0.7)]" />
                <PiWarningFill opacity={0} />
            </div>
        ),
        titleColor: 'text-gray-900',
        gradient: 'bg-gradient-to-r from-[#edc424] to-[#fe7902]',
        closeIconColor: 'text-yellow-300',
        closeIconHoverColor: 'hover:text-yellow-600',
        closeIconHoverBgColor: 'hover:bg-yellow-100',
    },
    info: {
        icon: (
            <div className="relative text-[2.5rem] flex-shrink-0">
                <div className="bg-white absolute top-[0.18em] left-[0.3em] w-[0.4em] h-[0.7em] z-0"></div>
                <BsFillInfoCircleFill className="text-blue-600 absolute z-10 drop-shadow-[0_0_6px_rgba(255,255,255,0.5)]" />
                <BsFillInfoCircleFill opacity={0} />
            </div>
        ),
        titleColor: 'text-gray-900',
        gradient: 'bg-gradient-to-r from-[#6391ff] to-[#a9d6f2]',
        closeIconColor: 'text-blue-600',
        closeIconHoverColor: 'hover:text-blue-600',
        closeIconHoverBgColor: 'hover:bg-blue-100',
    },
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ open, type = 'info', title, message, buttons, onClose, closeOnBackdropClick = true, closeOnEscape = true, showCloseButton = true, className = '' }) => {
    const dialogRef = useRef<HTMLDivElement>(null)
    const previouslyFocusedRef = useRef<HTMLElement | null>(null)

    // Handle Escape key
    useEffect(() => {
        if (!open || !onClose || !closeOnEscape) return

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [open, onClose, closeOnEscape])

    // Move focus into the dialog on open, restore it on close
    useEffect(() => {
        if (open) {
            previouslyFocusedRef.current = document.activeElement as HTMLElement
            dialogRef.current?.focus()
        } else {
            previouslyFocusedRef.current?.focus()
        }
    }, [open])

    // Handle backdrop click
    const handleBackdropClick = () => {
        if (closeOnBackdropClick) {
            onClose?.()
        }
    }

    if (!open) return null

    const config = typeConfig[type]

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <button type="button" className="absolute inset-0 bg-black bg-opacity-50 cursor-default" aria-label="Dismiss dialog" tabIndex={-1} onClick={handleBackdropClick} />
            <div
                ref={dialogRef}
                tabIndex={-1}
                className={`relative bg-white rounded-[0.417rem] overflow-hidden shadow-2xl max-w-md w-full mx-4 outline-none ${className}`}
                data-testid="confirm-dialog"
                aria-modal="true"
                aria-labelledby="dialog-title"
                aria-describedby={message ? 'dialog-message' : undefined}
            >
                {/* Content */}
                <div className="pb-3">
                    <div className={`p-3 ${config.gradient} flex gap-2 items-center relative`}>
                        {config.icon}
                        <h2 id="dialog-title" className={`${config.titleColor} w-full mr-5 text-[1rem] font-semibold leading-[1.3]`}>
                            {title}
                        </h2>

                        {showCloseButton && onClose && (
                            <button
                                onClick={onClose}
                                className={`absolute top-2 right-2 p-1 rounded-md transition-colors ${config.closeIconColor}  ${config.closeIconHoverColor} ${config.closeIconHoverBgColor}`}
                                aria-label="Close dialog"
                                type="button"
                            >
                                <IoClose size={20} />
                            </button>
                        )}
                    </div>

                    <div className="p-3 ">
                        {/* Message */}
                        {message && (
                            <div id="dialog-message" className="text-gray-600 text-sm max-h-[65vh] overflow-y-auto">
                                {typeof message === 'string' ? <p>{message}</p> : message}
                            </div>
                        )}
                        <hr className="mt-3 border-gray-300" />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 justify-center flex-wrap px-3">
                        {buttons.map((button, index) => (
                            <button
                                key={`${button.label}-${index}`}
                                onClick={button.onClick}
                                disabled={button.disabled || button.loading}
                                className={`relative px-3 py-1 rounded-[0.25rem] font-semibold text-[0.75rem] transition-all flex items-center gap-2
                  ${getButtonVariantClasses(button.variant)}
                  ${button.disabled || button.loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
                                type="button"
                            >
                                {button.loading && <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                                {button.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}

const getButtonVariantClasses = (variant?: ConfirmDialogButton['variant']): string => {
    switch (variant) {
        case 'primary':
            return 'bg-primary text-secondary hover:bg-opacity-90'
        case 'secondary':
            return 'bg-secondary text-white hover:bg-opacity-90'
        case 'danger':
            return 'bg-red-600 text-white hover:bg-red-700'
        case 'success':
            return 'bg-green-600 text-white hover:bg-green-700'
        case 'warning':
            return 'bg-yellow-600 text-white hover:bg-yellow-700'
        case 'ghost':
            return 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        default:
            return 'bg-primary text-secondary hover:bg-opacity-90'
    }
}

export default ConfirmDialog
